import * as THREE from 'three';
import { Subscription } from 'rxjs';
import { ICharacterAI } from '../../interfaces/ICharacterAI';
import { Character } from '../Character';
import { World } from '../../world/World';
import { Vehicle } from '../../vehicles/Vehicle';
import { Car } from '../../vehicles/Car';
import { TacticalCombatBehaviour } from './TacticalCombatBehaviour';
import { PedestrianBehavior } from './PedestrianBehavior';
import { Idle } from '../states/Idle';
import * as Utils from '../../core/FunctionLibrary';
import { LineOfSightService } from '../../world/LineOfSightService';
import { PoliceSirenAudio } from '../../vehicles/audio/PoliceSirenAudio';

type Phase = 'chasing' | 'stopping' | 'exiting' | 'fighting' | 'returning' | 'released';

const EXIT_RADIUS = 12;
const ENTRY_RADIUS = 3;
const STOP_SPEED_THRESHOLD = 0.5;

const EYE_HEIGHT = 1.5;

export class CopCarBehaviour implements ICharacterAI {
    public character: Character;
    public hasLoS = false;
    private phase: Phase = 'chasing';
    private tacticalCombat: TacticalCombatBehaviour | null = null;
    private subscription: Subscription | null = null;
    private siren: PoliceSirenAudio | null = null;

    constructor(
        private readonly world: World,
        private readonly car: Car,
        private readonly losService: LineOfSightService = new LineOfSightService(),
    ) {
        if (world.audioManager) {
            this.siren = new PoliceSirenAudio(world.audioManager, car);
            this.siren.play();
        }

        this.subscription = world.wantedLevelService?.wantedLevel$.subscribe(level => {
            if (level === 0) { this.setPhase('released'); }
            else if (level >= 1 && this.phase === 'released') { this.setPhase('chasing'); }
        }) ?? null;
    }

    public update(timeStep: number): void {
        if (!this.character) return;

        if (this.phase === 'released') { this.updateReleased(); return; }

        const player = this.world.player;
        if (!player || player.isDead) return;

        switch (this.phase) {
            case 'chasing':   this.updateChasing(player); break;
            case 'stopping':  this.updateStopping(); break;
            case 'exiting':   this.updateExiting(); break;
            case 'fighting':  this.updateFighting(player, timeStep); break;
            case 'returning': this.updateReturning(player); break;
        }

        this.updateHasLoS(player);
    }

    public destroy(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
        this.siren?.stop();
        this.siren = null;
    }

    private updateChasing(player: Character): void {
        if (!this.character.controlledObject) return;

        const vehiclePos = new THREE.Vector3();
        (this.character.controlledObject as unknown as THREE.Object3D).getWorldPosition(vehiclePos);

        const targetObject: THREE.Object3D = player.controlledObject
            ? (player.controlledObject as unknown as THREE.Object3D)
            : player;
        const targetPos = new THREE.Vector3();
        targetObject.getWorldPosition(targetPos);
        const dist = vehiclePos.distanceTo(targetPos);

        if (!player.controlledObject && dist < EXIT_RADIUS) {
            this.setPhase('stopping');
            const controlled = this.character.controlledObject;
            controlled.triggerAction('throttle', false);
            controlled.triggerAction('reverse', false);
            controlled.triggerAction('brake', true);
            return;
        }

        this.driveToward(targetPos);
    }

    private updateStopping(): void {
        const controlled = this.character.controlledObject;
        if (!controlled) return;

        const velocity = (controlled as unknown as Vehicle).collision.velocity;
        const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y + velocity.z * velocity.z);

        controlled.triggerAction('brake', true);

        if (speed < STOP_SPEED_THRESHOLD) {
            this.setPhase('exiting');
            this.character.exitVehicle();
        }
    }

    private updateExiting(): void {
        if (this.character.physics.isEnabled) {
            this.setPhase('fighting');
            this.tacticalCombat = new TacticalCombatBehaviour(this.world);
            this.tacticalCombat.character = this.character;
        }
    }

    private updateFighting(player: Character, timeStep: number): void {
        if (player.controlledObject) {
            this.setPhase('returning');
            this.tacticalCombat = null;
            return;
        }
        this.tacticalCombat?.update(timeStep);
    }

    private updateReturning(player: Character): void {
        if (!player.controlledObject) {
            this.setPhase('fighting');
            this.tacticalCombat = new TacticalCombatBehaviour(this.world);
            this.tacticalCombat.character = this.character;
            return;
        }

        const carPos = new THREE.Vector3();
        (this.car as unknown as THREE.Object3D).getWorldPosition(carPos);
        const charPos = this.character.getWorldPosition(new THREE.Vector3());
        const towardCar = new THREE.Vector3().subVectors(carPos, charPos);
        towardCar.y = 0;
        const dist = towardCar.length();

        if (dist < ENTRY_RADIUS) {
            if (this.car.seats[0].occupiedBy !== null) return;
            this.character.teleportToVehicle(this.car, this.car.seats[0]);
            this.setPhase('chasing');
        } else {
            this.character.setViewVector(towardCar.normalize());
            this.character.setOrientation(towardCar, false);
            this.character.triggerAction('up', true);
        }
    }

    private updateReleased(): void {
        if (this.character.controlledObject) {
            const controlled = this.character.controlledObject;
            controlled.triggerAction('throttle', false);
            controlled.triggerAction('reverse', false);
            controlled.triggerAction('brake', true);
            controlled.triggerAction('left', false);
            controlled.triggerAction('right', false);
        } else {
            this.character.setBehaviour(new PedestrianBehavior(undefined, this.world.navMesh ?? undefined));
            this.character.setState(new Idle(this.character));
        }
    }

    private setPhase(next: Phase): void {
        const wasChasing = this.phase === 'chasing';
        const willChase = next === 'chasing';
        this.phase = next;
        if (willChase && !wasChasing) { this.siren?.play(); }
        else if (!willChase && wasChasing) { this.siren?.stop(); }
    }

    private updateHasLoS(player: Character): void {
        if (!this.character || !this.world.physicsWorld) {
            this.hasLoS = false;
            return;
        }

        const copPos = this.character.getWorldPosition(new THREE.Vector3());
        copPos.y += EYE_HEIGHT;
        const playerPos = player.getWorldPosition(new THREE.Vector3());
        playerPos.y += EYE_HEIGHT;

        this.hasLoS = this.losService.hasLineOfSight(copPos, playerPos, this.world.physicsWorld);
    }

    private driveToward(targetPos: THREE.Vector3): void {
        const controlled = this.character.controlledObject;
        if (!controlled) return;

        const source = new THREE.Vector3();
        (controlled as unknown as THREE.Object3D).getWorldPosition(source);

        const viewVector = new THREE.Vector3().subVectors(targetPos, source);
        viewVector.y = 0;
        viewVector.normalize();

        const forward = new THREE.Vector3(0, 0, 1)
            .applyQuaternion((controlled as unknown as THREE.Object3D).quaternion);

        const velocity = (controlled as unknown as Vehicle).collision.velocity;
        const goingForward = forward.dot(Utils.threeVector(velocity)) > 0;
        const angle = Utils.getSignedAngleBetweenVectors(forward, viewVector);

        controlled.triggerAction('throttle', forward.dot(viewVector) >= 0);
        controlled.triggerAction('reverse', forward.dot(viewVector) < 0);

        if (Math.abs(angle) > 0.15) {
            if (forward.dot(viewVector) > 0 || goingForward) {
                controlled.triggerAction('left', angle > 0);
                controlled.triggerAction('right', angle <= 0);
            } else {
                controlled.triggerAction('right', angle > 0);
                controlled.triggerAction('left', angle <= 0);
            }
        } else {
            controlled.triggerAction('left', false);
            controlled.triggerAction('right', false);
        }
    }
}
